import type { ValueOf } from './util/typeUtils';

export const APPLICANT_ATTRIBUTION_BOTH = {
    ANTRAGSTELLER_1: 'ANTRAGSTELLER_1',
    ANTRAGSTELLER_2: 'ANTRAGSTELLER_2',
    BEIDE: 'BEIDE',
} as const;
export type APPLICANT_ATTRIBUTION_BOTH_TYPE = keyof typeof APPLICANT_ATTRIBUTION_BOTH;

export const APPLICANT_ATTRIBUTIONS = {
    ANTRAGSTELLER_1: 'ANTRAGSTELLER_1',
    ANTRAGSTELLER_2: 'ANTRAGSTELLER_2',
} as const;
export type APPLICANT_ATTRIBUTION = keyof typeof APPLICANT_ATTRIBUTIONS;

export const APPLICANT_ATTRIBUTION_KEYS = {
    ANTRAGSTELLER_1: 'antragsteller1',
    ANTRAGSTELLER_2: 'antragsteller2',
} as const;
export type APPLICANT_ATTRIBUTION_KEY = ValueOf<typeof APPLICANT_ATTRIBUTION_KEYS>;

export const APPLICANT_AGREEMENT_KEYS = {
    ADVERTISEMENT_CONSENT: 'advertisementConsentAgreed',
    DATA_PROTECTION: 'dataProtectionAgreed',
    EMAIL_VERIFICATION: 'emailVerified',
} as const;
export type APPLICANT_AGREEMENT_KEY = (typeof APPLICANT_AGREEMENT_KEYS)[keyof typeof APPLICANT_AGREEMENT_KEYS];

export const TITLES = {
    DOKTOR: 'DOKTOR',
    PROFESSOR: 'PROFESSOR',
} as const;
export type TITLE = keyof typeof TITLES;

export const LIVING_SITUATIONS = {
    BEI_DEN_ELTERN: 'BEI_DEN_ELTERN',
    IM_EIGENEN_HAUS: 'IM_EIGENEN_HAUS',
    MIETFREI: 'MIETFREI',
    ZUR_MIETE: 'ZUR_MIETE',
    ZUR_UNTERMIETE: 'ZUR_UNTERMIETE',
} as const;
export type LIVING_SITUATION = keyof typeof LIVING_SITUATIONS;

export const EMPLOYMENT_TYPES = {
    ANGESTELLTER: 'ANGESTELLTER',
    ARBEITER: 'ARBEITER',
    ARBEITSLOSER: 'ARBEITSLOSER',
    AZUBI: 'AZUBI',
    BEAMTER: 'BEAMTER',
    FREIBERUFLER: 'FREIBERUFLER',
    HAUSFRAU: 'HAUSFRAU',
    RENTNER: 'RENTNER',
    SELBSTSTAENDIGER: 'SELBSTSTAENDIGER',
    STUDENT: 'STUDENT',
} as const;
export type EMPLOYMENT_TYPE = keyof typeof EMPLOYMENT_TYPES;

export const EMPLOYMENT_LIMITATIONS = {
    BEFRISTET: 'BEFRISTET',
    UNBEFRISTET: 'UNBEFRISTET',
} as const;
export type EMPLOYMENT_LIMITATION = keyof typeof EMPLOYMENT_LIMITATIONS;

export const SALUTATIONS = {
    DIVERS: 'DIVERS',
    FRAU: 'FRAU',
    HERR: 'HERR',
} as const;
export type SALUTATION = keyof typeof SALUTATIONS;

export const GENDERS = {
    FEMALE: 'FEMALE',
    MALE: 'MALE',
} as const;
export type GENDER = keyof typeof GENDERS;

export const PERSONAL_STATUS = {
    EHEAEHNLICHE_LEBENSGEMEINSCHAFT: 'EHEAEHNLICHE_LEBENSGEMEINSCHAFT',
    EINGETRAGENE_LEBENSPARTNERSCHAFT: 'EINGETRAGENE_LEBENSPARTNERSCHAFT',
    GESCHIEDEN: 'GESCHIEDEN',
    GETRENNT_LEBEND: 'GETRENNT_LEBEND',
    LEDIG: 'LEDIG',
    VERHEIRATET: 'VERHEIRATET',
    VERWITWET: 'VERWITWET',
} as const;
export type PERSONAL_STATUS_TYPE = keyof typeof PERSONAL_STATUS;

export const EUROPACE_RESIDENCE_PERMIT_TYPES = {
    AUFENTHALTSERLAUBNIS: 'AUFENTHALTSERLAUBNIS',
    ERLAUBNIS_ZUM_DAUERAUFENTHALT_EU: 'ERLAUBNIS_ZUM_DAUERAUFENTHALT_EU',
    NIEDERLASSUNGSERLAUBNIS: 'NIEDERLASSUNGSERLAUBNIS',
    VISUM: 'VISUM',
} as const;
export type EUROPACE_RESIDENCE_PERMIT_TYPE = keyof typeof EUROPACE_RESIDENCE_PERMIT_TYPES;

export const RESIDENCE_PERMIT_TYPES = {
    LIMITED_RESIDENCE_PERMIT_PARAGRAPH_19A: 'LIMITED_RESIDENCE_PERMIT_PARAGRAPH_19A',
    LIMITED_RESIDENCE_PERMIT_PARAGRAPH_OTHERS: 'LIMITED_RESIDENCE_PERMIT_PARAGRAPH_OTHERS',
    PERMANENT_RESIDENCE_PERMIT: 'PERMANENT_RESIDENCE_PERMIT',
    RESIDENCE_CARD: 'RESIDENCE_CARD',
    RESIDENCE_PERMIT_CARD: 'RESIDENCE_PERMIT_CARD',
    RESIDENCE_PERMIT_OTHER: 'RESIDENCE_PERMIT_OTHER',
    RESIDENCE_PERMIT_SWITZERLAND: 'RESIDENCE_PERMIT_SWITZERLAND',
    UNLIMITED_RESIDENCE_PERMIT: 'UNLIMITED_RESIDENCE_PERMIT',
} as const;
export type RESIDENCE_PERMIT_TYPE = keyof typeof RESIDENCE_PERMIT_TYPES;

// RESIDENCE_PERMIT_SWITZERLAND can be limited or unlimited residence permit - it is therefore not included in the following types
export const UNLIMITED_RESIDENCE_PERMIT_TYPES = [
    RESIDENCE_PERMIT_TYPES.PERMANENT_RESIDENCE_PERMIT,
    RESIDENCE_PERMIT_TYPES.UNLIMITED_RESIDENCE_PERMIT,
    RESIDENCE_PERMIT_TYPES.RESIDENCE_PERMIT_CARD,
] as const;
export type UNLIMITED_RESIDENCE_PERMIT_TYPE = typeof UNLIMITED_RESIDENCE_PERMIT_TYPES;
export const LIMITED_RESIDENCE_PERMIT_TYPES = [
    RESIDENCE_PERMIT_TYPES.LIMITED_RESIDENCE_PERMIT_PARAGRAPH_19A,
    RESIDENCE_PERMIT_TYPES.LIMITED_RESIDENCE_PERMIT_PARAGRAPH_OTHERS,
    RESIDENCE_PERMIT_TYPES.RESIDENCE_CARD,
    RESIDENCE_PERMIT_TYPES.RESIDENCE_PERMIT_OTHER,
] as const;
export type LIMITED_RESIDENCE_PERMIT_TYPE = typeof LIMITED_RESIDENCE_PERMIT_TYPES;

export const EMPLOYMENT_INDUSTRY = {
    BAUGEWERBE: 'BAUGEWERBE',
    DIENSTLEISTUNGEN: 'DIENSTLEISTUNGEN',
    ENERGIE_WASSERVERSORGUNG_BERGBAU: 'ENERGIE_WASSERVERSORGUNG_BERGBAU',
    ERZIEHUNG_UNTERRICHT: 'ERZIEHUNG_UNTERRICHT',
    GEBIETSKOERPERSCHAFTEN: 'GEBIETSKOERPERSCHAFTEN',
    GEMEINNUETZIGE_ORGANISATION: 'GEMEINNUETZIGE_ORGANISATION',
    GESUNDHEIT_SOZIALWESEN: 'GESUNDHEIT_SOZIALWESEN',
    HANDEL: 'HANDEL',
    HOTEL_GASTRONOMIE: 'HOTEL_GASTRONOMIE',
    INFORMATION_KOMMUNIKATION: 'INFORMATION_KOMMUNIKATION',
    KREDITINSTITUTE_VERSICHERUNGEN: 'KREDITINSTITUTE_VERSICHERUNGEN',
    KULTUR_SPORT_UNTERHALTUNG: 'KULTUR_SPORT_UNTERHALTUNG',
    LANDWIRTSCHAFT_FORSTWIRTSCHAFT_FISCHEREI: 'LANDWIRTSCHAFT_FORSTWIRTSCHAFT_FISCHEREI',
    OEFFENTLICHER_DIENST: 'OEFFENTLICHER_DIENST',
    PRIVATE_HAUSHALTE: 'PRIVATE_HAUSHALTE',
    VERARBEITENDES_GEWERBE: 'VERARBEITENDES_GEWERBE',
    VERKEHR_LOGISTIK: 'VERKEHR_LOGISTIK',
} as const;

export const TYPE_OF_EMPLOYMENT = {
    BEFRISTET: 'BEFRISTET',
    UNBEFRISTET: 'UNBEFRISTET',
} as const;

export const LEGITIMATION_TYPES = {
    TELEPHONE: 'telephone',
} as const;

export const APPLICANT_AGREEMENT_TYPES = {
    ADVERTISEMENT_CONSENT: 'ADVERTISEMENT_CONSENT',
    DATA_PROTECTION: 'DATA_PROTECTION',
    EMAIL_VERIFICATION: 'EMAIL_VERIFICATION',
} as const;

export const APPLICANT_CREDIT_RATING_REQUESTED_STATUSES = {
    FAILED: 'FAILED',
    MERGED: 'MERGED',
    NOT_REQUESTED: 'NOT_REQUESTED',
    PENDING: 'PENDING',
    RECEIVED: 'RECEIVED',
    REJECTED: 'REJECTED',
    UNCHANGED: 'UNCHANGED',
} as const;

export type APPLICANT_CREDIT_RATING_REQUESTED_STATUS = ValueOf<typeof APPLICANT_CREDIT_RATING_REQUESTED_STATUSES>;
